/**
 * TeamLocalResources — the Local Resources branch of Reference > Team (TeamSection.jsx), Matt
 * 2026-10-27: "under the Team tab we should always list the local resources for the project like
 * the repro companies in the area… an action item to prepare a print order and … track those orders
 * under their node so we have a record of what we requested over the course of the project."
 *
 *   Local Resources                         sec:team:group:local-resources
 *     <Print shop>  ⋯ Prepare print order…  sec:team:shop:<orgId>
 *       <date> · Estimate first · Sent      sec:team:printorder:<id>
 *         Files / Delivery / Sent to / Email — the order exactly as sent
 *
 * A print shop is any Organization whose Type includes "Reprographics" (services/printOrders.js).
 * Orders are read once per project into ctx.printOrderCache (TeamSection loads it).
 * docs/PRINT_ORDERS.md.
 */

import { wf, saveViewState } from '../outline/state.js'
import { ctx } from '../outline/ctx.js'
import { useActionMenu, ActionMenu, ActionMenuButton, ActionMenuItem, ActionMenuDivider } from './ActionMenu.jsx'
import {
  PRINT_APPROVALS, PRINT_STATUSES, optLabel, itemSpec, deliveryLine,
} from '../../../api/_printOrderOptions.js'

const MUTED = '#7a9aaa'

function toggleFn(key, collapsed) {
  return (e) => { e.stopPropagation(); wf.collapsed[key] = !collapsed; saveViewState(); window.dispatchEvent(new Event('outline-render')) }
}

function Filler({ text, italic }) {
  return (
    <div className="wf-node wf-node--item">
      <div className="wf-row">
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <span className="wf-bullet wf-bullet--item" style={{ opacity: 0.3 }}></span>
        <div className="wf-text-wrap">
          <span className="wf-node-text" style={{ color: MUTED, fontStyle: italic ? 'italic' : 'normal' }}>{text}</span>
        </div>
      </div>
    </div>
  )
}

function Leaf({ k, text, meta, href, color }) {
  return (
    <div className="wf-node wf-node--item" data-key={k}>
      <div className="wf-row">
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <span className="wf-bullet wf-bullet--item"></span>
        <div className="wf-text-wrap">
          {href
            ? <a className="wf-node-text" href={href} target="_blank" rel="noreferrer" style={{ color: 'var(--aj-accent)', textDecoration: 'none' }}>{text}</a>
            : <span className="wf-node-text" style={{ ...(color ? { color } : {}), whiteSpace: 'pre-wrap' }}>{text}</span>}
          {meta && <span className="wf-meta" style={{ marginLeft: 8, color: MUTED, fontSize: 11, fontFamily: '"DM Sans",sans-serif' }}>{meta}</span>}
        </div>
      </div>
    </div>
  )
}

function Branch({ k, label, meta, children }) {
  const collapsed = wf.collapsed[k] !== false
  return (
    <div className={`wf-node wf-node--section${collapsed ? ' wf-node--collapsed' : ''}`} data-key={k}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggleFn(k, collapsed)}>
          <span className={`wf-tri-icon ${collapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <span className="wf-bullet wf-bullet--section"></span>
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--section">{label}</span>
          {meta && <span className="wf-meta" style={{ marginLeft: 8, color: MUTED, fontSize: 11, fontFamily: '"DM Sans",sans-serif' }}>{meta}</span>}
        </div>
      </div>
      {!collapsed && <div className="wf-children">{children}</div>}
    </div>
  )
}

export function orderDate(o) {
  const d = o && o.sent_at ? String(o.sent_at).slice(0, 10).split('-') : null
  return d ? `${+d[1]}/${+d[2]}/${d[0]}` : ''
}

/** "10/27/2026 · 3 files · Estimate first" — an order's one-line label. */
export function orderLabel(o) {
  const n = (o.items || []).length
  return [orderDate(o), n + ' file' + (n === 1 ? '' : 's'), optLabel(PRINT_APPROVALS, o.approval)].filter(Boolean).join(' · ')
}

export function statusLabel(o) { return optLabel(PRINT_STATUSES, o.status) }

function statusColor(s) {
  return s === 'failed' ? '#C0392B' : s === 'complete' ? '#1D9E75' : s === 'estimate_received' ? '#b26a00' : MUTED
}

export function shopOrders(orgId) {
  return ((ctx.printOrderCache || []).filter(o => o.org_airtable_id === orgId))
}

/** The order as sent, as outline rows (also the printorder zoom's content). */
export function PrintOrderRecordRows({ order }) {
  const k = 'po:' + order.id
  const items = order.items || []
  const to = [...(order.recipients || []).map(r => r.name ? r.name + ' <' + r.email + '>' : r.email), order.general_email].filter(Boolean)
  return (
    <>
      <Branch k={k + ':files'} label="Files" meta={String(items.length)}>
        {items.map((it, i) => (
          <Leaf key={i} k={k + ':file:' + i} text={it.name + (it.sheets ? ' (' + it.sheets + ' sheets)' : '')} href={it.url || undefined}
            meta={itemSpec(it) + (it.note ? ' · ' + it.note : '')} />
        ))}
      </Branch>
      <Leaf k={k + ':approval'} text={optLabel(PRINT_APPROVALS, order.approval)}
        meta={(PRINT_APPROVALS.find(a => a.v === order.approval) || {}).d} />
      <Leaf k={k + ':delivery'} text={deliveryLine(order.delivery_method, order.delivery_address)}
        meta={order.need_by ? 'needed by ' + orderDate({ sent_at: order.need_by }) : null} />
      {order.notes && <Leaf k={k + ':notes'} text={order.notes} meta="note" />}
      <Leaf k={k + ':to'} text={'Sent to ' + (to.join(', ') || 'no one')} meta={order.sender_name ? 'by ' + order.sender_name : null} />
      {order.email_status && order.email_status !== 'sent' && (
        <Leaf k={k + ':err'} text={'Email did not go: ' + (order.email_error || order.email_status)} color="#C0392B" />
      )}
      {order.status_note && <Leaf k={k + ':snote'} text={order.status_note} meta={statusLabel(order)} />}
      {order.email_text && (
        <Branch k={k + ':email'} label="Email as sent" meta={order.email_subject}>
          <Leaf k={k + ':emailtext'} text={order.email_text} />
        </Branch>
      )}
    </>
  )
}

/** The ⋯ items every order offers (row menu and the order zoom's title menu). */
export function PrintOrderMenuItems({ order, actions, close }) {
  const run = (fn) => () => { if (close) close(); fn() }
  return (
    <>
      {PRINT_STATUSES.filter(s => s.v !== 'failed' && s.v !== order.status).map(s => (
        <ActionMenuItem key={s.v} onClick={run(() => actions.setStatus(order, s.v))}>Mark {s.l.toLowerCase()}</ActionMenuItem>
      ))}
      <ActionMenuDivider />
      <ActionMenuItem onClick={run(() => actions.orderAgain(order))}>Order again…</ActionMenuItem>
      <ActionMenuItem danger onClick={run(() => actions.deleteOrder(order))}>Delete this record</ActionMenuItem>
    </>
  )
}

function PrintOrderRow({ order, actions }) {
  const key = 'sec:team:printorder:' + order.id
  const collapsed = wf.collapsed[key] !== false
  const menu = useActionMenu()
  return (
    <div className={`wf-node wf-node--item${collapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggleFn(key, collapsed)}>
          <span className={`wf-tri-icon ${collapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button className="wf-bullet wf-bullet--item" title="Focus on this order" data-kbzoom="1"
          onClick={e => { e.stopPropagation(); ctx.zoomToSection(key, { root: 'team' }) }}></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text">{orderLabel(order)}</span>
          <span className="wf-meta" style={{ marginLeft: 8, color: statusColor(order.status), fontSize: 11, fontFamily: '"DM Sans",sans-serif' }}>{statusLabel(order)}</span>
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <PrintOrderMenuItems order={order} actions={actions} close={menu.close} />
      </ActionMenu>
      {!collapsed && <div className="wf-children"><PrintOrderRecordRows order={order} /></div>}
    </div>
  )
}

/** A shop's orders on this project (the shop row's children, and the shop zoom's content). */
export function ShopOrdersList({ org, actions }) {
  if (!ctx.printOrderCache) return <Filler text="Loading…" />
  const orders = shopOrders(org.id)
  if (!orders.length) return <Filler text="No print orders to this shop on this project yet." italic />
  return <>{orders.map(o => <PrintOrderRow key={o.id} order={o} actions={actions} />)}</>
}

function ShopRow({ org, actions }) {
  const key = 'sec:team:shop:' + org.id
  const collapsed = wf.collapsed[key] !== false
  const menu = useActionMenu()
  const n = shopOrders(org.id).length
  const name = org.fields['Organization Name'] || 'Print shop'
  const cities = (ctx.printShopCities || {})[org.id] || []
  const serves = cities.length ? 'serves ' + cities.join(', ') : 'every project'
  return (
    <div className={`wf-node wf-node--section${collapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggleFn(key, collapsed)}>
          <span className={`wf-tri-icon ${collapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button className="wf-bullet wf-bullet--section" title={'Focus on ' + name} data-kbzoom="1"
          onClick={e => { e.stopPropagation(); ctx.zoomToSection(key, { root: 'team' }) }}></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--section">{name}</span>
          <span className="wf-meta" style={{ marginLeft: 8, color: MUTED, fontSize: 11, fontFamily: '"DM Sans",sans-serif' }}>
            {serves}{ctx.printOrderCache ? ' · ' + n + ' order' + (n === 1 ? '' : 's') : ''}
          </span>
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={() => { menu.close(); actions.prepare(org) }}>Prepare print order…</ActionMenuItem>
        <ActionMenuDivider />
        <ActionMenuItem onClick={() => { menu.close(); actions.editCities(org) }}>Cities served…</ActionMenuItem>
      </ActionMenu>
      {!collapsed && <div className="wf-children"><ShopOrdersList org={org} actions={actions} /></div>}
    </div>
  )
}

/** The shops themselves (the group's children, and the group zoom's content): the ones serving
 *  this project's city, then the rest under a closed "Other shops" branch. */
export function LocalResourcesList({ shops, otherShops, actions }) {
  if (!shops) return <Filler text="Loading…" />
  const others = otherShops || []
  if (!shops.length && !others.length) {
    return <Filler italic text={'No print shops yet. In Contacts, give an organization the type "Reprographics" and it shows here on every project.'} />
  }
  return (
    <>
      {!shops.length && <Filler italic text={'No print shop lists ' + (ctx.printProjectCity || "this project's city") + ' yet.'} />}
      {shops.map(org => <ShopRow key={org.id} org={org} actions={actions} />)}
      {others.length > 0 && (
        <Branch k="sec:team:localother" label="Other shops"
          meta={others.length + ' not serving ' + (ctx.printProjectCity || "this project's city")}>
          {others.map(org => <ShopRow key={org.id} org={org} actions={actions} />)}
        </Branch>
      )}
    </>
  )
}

export default function LocalResourcesGroup({ shops, otherShops, actions }) {
  const key = 'sec:team:group:local-resources'
  const collapsed = wf.collapsed[key] !== false
  const menu = useActionMenu()
  return (
    <div className={`wf-node wf-node--section${collapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggleFn(key, collapsed)}>
          <span className={`wf-tri-icon ${collapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button className="wf-bullet wf-bullet--section" title="Focus on Local Resources" data-kbzoom="1"
          onClick={e => { e.stopPropagation(); ctx.zoomToSection(key, { root: 'team' }) }}></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--section">Local Resources</span>
          {shops && <span className="wf-prog" style={{ color: MUTED, marginLeft: 8 }}>{shops.length}</span>}
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={() => { menu.close(); window.dispatchEvent(new CustomEvent('open-contacts-panel')) }}>Add a print shop in Contacts…</ActionMenuItem>
      </ActionMenu>
      {!collapsed && <div className="wf-children"><LocalResourcesList shops={shops} otherShops={otherShops} actions={actions} /></div>}
    </div>
  )
}
